import React, { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon, ChevronRightIcon, ViewfinderCircleIcon } from '@heroicons/react/24/outline';

export interface CollapsibleFilterSectionProps {
  filterId: string;
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  isFocused?: boolean;
  hasActiveValues?: boolean;
  columnId: string | null;
  onLocateColumn?: (columnId: string) => void;
  isColumnVisible?: boolean;
}

export const CollapsibleFilterSection: React.FC<CollapsibleFilterSectionProps> = ({
  filterId,
  label,
  expanded,
  onToggle,
  children,
  isFocused = false,
  hasActiveValues = false,
  columnId,
  onLocateColumn,
  isColumnVisible = true,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocused && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isFocused]);

  const handleLocateClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (columnId && onLocateColumn && isColumnVisible) {
      onLocateColumn(columnId);
    }
  };

  const showLocate = columnId != null && onLocateColumn;
  const locateDisabled = !isColumnVisible;

  return (
    <div
      ref={sectionRef}
      className={clsx(
        'transition-all',
        isFocused && 'rounded ring-2 ring-blue-500 ring-offset-4 animate-focus-ring-out'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={clsx(
          'group flex w-full items-center gap-2 text-left text-sm font-medium',
          hasActiveValues ? 'text-blue-600' : 'text-gray-700'
        )}
        aria-expanded={expanded}
        aria-controls={`filter-section-${filterId}`}
        id={`filter-section-header-${filterId}`}
      >
        {expanded ? (
          <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" aria-hidden />
        )}
        <span className="min-w-0 truncate">{label}</span>
        {showLocate && (
          <span
            className={clsx(
              'flex items-center justify-center flex-shrink-0 transition-opacity',
              locateDisabled ? 'opacity-0 group-hover:opacity-40 cursor-not-allowed' : 'opacity-0 group-hover:opacity-100 cursor-pointer'
            )}
            title={locateDisabled ? 'Column hidden' : 'Locate column'}
            onClick={handleLocateClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (!locateDisabled) handleLocateClick(e as unknown as React.MouseEvent);
              }
            }}
            role="button"
            tabIndex={locateDisabled ? -1 : 0}
          >
            <ViewfinderCircleIcon className="h-3.5 w-3.5 text-gray-500 hover:text-blue-600" />
          </span>
        )}
      </button>
      {expanded && (
        <div
          id={`filter-section-${filterId}`}
          className="pt-3 mb-3"
          role="region"
          aria-labelledby={`filter-section-header-${filterId}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};
